import { useMemo, useState } from 'react';
import { Loader2, FileText, CheckCircle2, Copy } from 'lucide-react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from '@sighthost/ui';
import {
  salesService,
  type IssueQuotationResult,
} from '@/domains/sales/services/salesService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
// One definition of the catalogue + the Studio deposit maths, shared with
// SalesAddCustomerPage (BRD FR-5). Previously local to this file.
import {
  SALES_SKUS,
  BUNDLE_ITEMS,
  STUDIO_TOTAL_CENTS,
  STUDIO_DEPOSIT_FLOOR_CENTS,
  installmentPreview,
  isDepositValid,
  zar,
  type SkuCode,
} from '@/domains/sales/studioQuotation';

/**
 * IssueQuotationModal — ST-4 (2026-07-17). Lets an agent issue a QUO- document
 * for a service SKU to the current lead. Studio Starter exposes the flexible
 * deposit (floor R500, Q1) + 3/6/12-month plan with a live instalment preview
 * (floor variant — the sub-rand residual is a rounding discount, ST-6).
 *
 * Hosting quotations keep their richer flow on the Add Customer page (domain
 * search + registrant capture don't fit a modal); the EF supports hosting for
 * API completeness.
 */





interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerProfileId: string;
  /** Called after a quotation is successfully issued (reload the order card). */
  onIssued: () => void;
  /**
   * The lead's current LIVE quotation, when one exists. Presence of this turns
   * the modal into a conversion: the agent is offered "replace it", on by
   * default, and the EF cancels it once the new quotation exists.
   */
  existingQuote?: {
    invoiceId: string;
    quoteNumber: string;
    /** e.g. "SightHost Entry" — what the customer was quoted before. */
    packageLabel: string | null;
    amountZar: number | null;
    domain: string | null;
  } | null;
}

export function IssueQuotationModal({
  open,
  onOpenChange,
  customerProfileId,
  onIssued,
  existingQuote,
}: Props) {
  const { isManagerOrAdmin } = useStaffRole();
  const [sku, setSku] = useState<SkuCode>('studio-starter');
  // W2 / GA-D1 (2026-08-10): agent discount. 15/20% options are disabled for
  // plain agents in the UI AND rejected by the EF (defence-in-depth, same
  // pattern as SALES-LEAK-1's effectiveTab).
  const [discountPct, setDiscountPct] = useState(0);
  // Default ON when the lead already holds a live quotation. Leaving the old one
  // payable is the dangerous default: the customer would hold two pay links and
  // could settle the wrong package.
  const [supersede, setSupersede] = useState(true);
  const [studioMode, setStudioMode] = useState<'plan' | 'full'>('plan');
  const [planMonths, setPlanMonths] = useState<3 | 6 | 12>(6);
  const [depositRand, setDepositRand] = useState('500');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IssueQuotationResult | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const depositCents = Math.round((Number(depositRand) || 0) * 100);
  const depositValid = isDepositValid(depositCents);

  const preview = useMemo(() => {
    if (sku !== 'studio-starter' || studioMode !== 'plan') return null;
    const p = installmentPreview(depositCents, planMonths);
    if (!p) return null;
    // Local shape kept for the existing JSX below.
    return { balance: p.balanceCents, installment: p.installmentCents, rounding: p.roundingCents };
  }, [sku, studioMode, depositCents, planMonths]);

  const canSubmit =
    !busy && (sku !== 'studio-starter' || studioMode === 'full' || depositValid);

  // Discounts apply to one-off SKU / bundle quotations only — the Studio
  // payment plan carries instalment maths the EF refuses to discount (W2-D1).
  const discountApplicable = !(sku === 'studio-starter' && studioMode === 'plan');

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const bundleItems = BUNDLE_ITEMS[sku];
      const res = await salesService.issueQuotation({
        customer_profile_id: customerProfileId,
        ...(bundleItems ? { items: bundleItems } : { sku_code: sku }),
        ...(sku === 'studio-starter' && studioMode === 'plan'
          ? { plan_months: planMonths, deposit_amount_cents: depositCents }
          : {}),
        ...(discountApplicable && discountPct > 0 ? { discount_pct: discountPct } : {}),
        ...(existingQuote && supersede
          ? { supersede_quotation_id: existingQuote.invoiceId }
          : {}),
      });
      setResult(res);
      onIssued();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function copyPayLink() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.pay_url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 4000);
    } catch {
      setError('Could not copy — select the link manually.');
    }
  }

  function close() {
    onOpenChange(false);
    // Reset for the next open (after the close animation).
    setTimeout(() => {
      setResult(null);
      setError(null);
      setBusy(false);
    }, 300);
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && close()}
      title="Issue quotation"
      description="The customer receives a QUO- document by email — paying it accepts the quotation and confirms the order."
    >
      <ModalBody>
        {result ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-md border border-success/30 bg-success/5 p-3 text-sm text-success">
              <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden />
              Quotation <strong>{result.quote_number}</strong> for{' '}
              {zar(Math.round(result.total_zar * 100))} emailed to {result.emailed_to}.
            </div>
            {result.superseded_quote_number && (
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
                <p className="text-foreground">
                  <strong>{result.superseded_quote_number}</strong> has been cancelled — its
                  pay-link no longer works, so the customer can only pay the new one.
                </p>
                {result.domain_carried_forward && (
                  <p className="mt-1 text-muted-foreground">
                    <strong>{result.domain_carried_forward}</strong> has been carried over and
                    will be registered as part of the Studio package at delivery.
                  </p>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Valid until {new Date(result.quote_expires_at).toLocaleDateString('en-ZA')}.
              On the call now? Copy the pay-link and read it out — no sign-in needed.
            </p>
            <Button onClick={copyPayLink} variant="outline" size="sm">
              <Copy className="h-4 w-4" /> {linkCopied ? 'Copied!' : 'Copy pay-link'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {existingQuote && (
              <div className="rounded-md border border-warning/40 bg-warning/5 p-3">
                <p className="text-xs font-medium text-foreground">
                  This lead already has a live quotation:{' '}
                  <strong>{existingQuote.quoteNumber}</strong>
                  {existingQuote.packageLabel ? ` — ${existingQuote.packageLabel}` : ''}
                  {existingQuote.amountZar !== null
                    ? ` (${zar(Math.round(existingQuote.amountZar * 100))})`
                    : ''}
                </p>
                <label className="mt-2 flex items-start gap-2 text-xs text-foreground">
                  <input
                    type="checkbox"
                    checked={supersede}
                    onChange={(e) => setSupersede(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Replace it with this new quotation
                    {existingQuote.domain ? (
                      <>
                        {' '}— <strong>{existingQuote.domain}</strong> carries over to the new
                        package
                      </>
                    ) : null}
                    .
                  </span>
                </label>
                {!supersede && (
                  <p className="mt-2 text-xs text-danger">
                    Leaving it live means the customer holds two pay-links and could pay the
                    wrong package. Only uncheck this if you intend to sell both.
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-medium text-ink" htmlFor="quote-sku">
                Service
              </label>
              <select
                id="quote-sku"
                value={sku}
                onChange={(e) => setSku(e.target.value as SkuCode)}
                className="h-9 w-full rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {SALES_SKUS.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.label}{s.hint ? ` — ${s.hint}` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Hosting quotations: use the Add Customer flow (domain search lives there).
              </p>
            </div>

            {discountApplicable && (
              <div>
                <label className="mb-1 block text-xs font-medium text-ink" htmlFor="quote-discount">
                  Discount
                </label>
                <select
                  id="quote-discount"
                  value={discountPct}
                  onChange={(e) => setDiscountPct(Number(e.target.value))}
                  className="h-9 w-full rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <option value={0}>No discount</option>
                  <option value={5}>5%</option>
                  <option value={10}>10%</option>
                  <option value={15} disabled={!isManagerOrAdmin}>
                    15%{isManagerOrAdmin ? '' : ' — manager only'}
                  </option>
                  <option value={20} disabled={!isManagerOrAdmin}>
                    20%{isManagerOrAdmin ? '' : ' — manager only'}
                  </option>
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  Shows as its own line on the quotation. Above 10% needs a sales manager.
                </p>
              </div>
            )}

            {sku === 'studio-starter' && (
              <>
                <div className="flex gap-4 text-sm">
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name="studio-mode"
                      checked={studioMode === 'plan'}
                      onChange={() => setStudioMode('plan')}
                    />
                    Deposit + monthly plan
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name="studio-mode"
                      checked={studioMode === 'full'}
                      onChange={() => setStudioMode('full')}
                    />
                    Full payment (R3,990)
                  </label>
                </div>

                {studioMode === 'plan' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-ink" htmlFor="quote-deposit">
                        Deposit (R) — your call, min R500
                      </label>
                      <Input
                        id="quote-deposit"
                        type="number"
                        min={500}
                        max={3989}
                        step={50}
                        value={depositRand}
                        onChange={(e) => setDepositRand(e.target.value)}
                      />
                      {!depositValid && (
                        <p className="mt-1 text-xs text-danger">
                          Between R500 and R3,989.99.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-ink" htmlFor="quote-months">
                        Plan length
                      </label>
                      <select
                        id="quote-months"
                        value={planMonths}
                        onChange={(e) => setPlanMonths(Number(e.target.value) as 3 | 6 | 12)}
                        className="h-9 w-full rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <option value={3}>3 months</option>
                        <option value={6}>6 months</option>
                        <option value={12}>12 months</option>
                      </select>
                    </div>
                  </div>
                )}

                {preview && (
                  <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
                    <div className="flex justify-between py-0.5">
                      <span className="text-muted-foreground">Studio Starter</span>
                      <span>{zar(STUDIO_TOTAL_CENTS)}</span>
                    </div>
                    <div className="flex justify-between py-0.5">
                      <span className="text-muted-foreground">Deposit now</span>
                      <span>({zar(depositCents)})</span>
                    </div>
                    <div className="flex justify-between py-0.5">
                      <span className="text-muted-foreground">Plan</span>
                      <span className="font-semibold">
                        {planMonths} × {zar(preview.installment)}/month
                      </span>
                    </div>
                    {preview.rounding > 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Includes a {zar(preview.rounding)} rounding discount.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {error && (
              <div className="rounded-md border border-danger/30 bg-danger/5 p-2 text-xs text-danger">
                {error}
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="outline" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!canSubmit} loading={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Issue quotation
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

export default IssueQuotationModal;
